/**
 * useGet hook - Fetch data với React hooks
 * Tự động refetch khi cần thiết
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  UseGetOptions,
  UseGetResult,
  RequestOptions,
  ApiError,
} from '../types';
import type { HttpClient } from '../https/client';

export function useGet<TData = unknown>(
  client: HttpClient,
  path: string,
  query?: RequestOptions['query'],
  options?: UseGetOptions
): UseGetResult<TData> {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const enabled = options?.enabled ?? true;
  const refetchInterval = options?.refetchInterval;
  const refetchOnWindowFocus = options?.refetchOnWindowFocus ?? false;

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setIsLoading(false);

      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();

    abortControllerRef.current = controller;
    setIsLoading(true);
    setError(null);

    try {
      const result = await client.get<TData>(path, query, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      setData(result);
    } catch (err) {
      if (controller.signal.aborted || (err as Error).name === 'AbortError') {
        return;
      }

      setError(err as ApiError);
      setData(undefined);
    } finally {
      if (abortControllerRef.current === controller) {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [client, path, query, enabled]);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchData();
    });

    return () => {
      cancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [fetchData]);

  // Refetch interval
  useEffect(() => {
    if (refetchInterval && refetchInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchData();
      }, refetchInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [refetchInterval, fetchData]);

  // Refetch on window focus
  useEffect(() => {
    if (!refetchOnWindowFocus) return;

    const handleFocus = () => {
      fetchData();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchOnWindowFocus, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
